import React, { useCallback } from 'react';

import ClinicalNotePainSection from './ClinicalNotePainSection.jsx';
import ClinicalNoteRomSection from './ClinicalNoteRomSection.jsx';
import ClinicalNoteStrengthSection from './ClinicalNoteStrengthSection.jsx';
import ClinicalNoteGoalSection from './ClinicalNoteGoalSection.jsx';
import ClinicalNoteContentSection from './ClinicalNoteContentSection.jsx';
import ClinicalNoteTreatmentSection from './ClinicalNoteTreatmentSection.jsx';
import ClinicalNoteCollapsibleSection from './ClinicalNoteCollapsibleSection.jsx';
import useClinicalSectionStatuses from './useClinicalSectionStatuses.js';

/**
 * The Daily Note's ordered sections. Use the same sequence for rendering and
 * accordion previous/next navigation so the two cannot drift apart.
 */
const DAILY_SECTION_KEYS = [
  'pain',
  'rom',
  'strength',
  'goals',
  'content',
  'treatment',
];

/**
 * Compact Daily clinical-note form.
 *
 * Pain opens by default; all other sections start collapsed. Collapsed
 * sections must stay mounted in ClinicalNoteCollapsibleSection so their
 * autosave handlers remain registered with the visit navigation coordinator.
 *
 * Goals remain editable here. Rules about which sections appear in the
 * finalized Daily Note PDF are owned by the backend PDF generation workflow.
 *
 * This wrapper does not determine whether Pain, ROM, or Strength changed
 * compared with the previous visit. Any combined unchanged-values warning
 * must be implemented using reliable cross-section clinical data and backend
 * validation, rather than inferred from accordion status badges.
 */
export default function ClinicalNoteDailyForm({
  clinicalNoteId,
  disabled = false,
  onSaved,
  onVersionChange,
  navigationCoordinator = null,
}) {
  const resetKey = String(clinicalNoteId ?? 'new');
  const accordionGroup = `clinical-note-${resetKey}`;
  const { statuses, refreshSectionStatuses } =
    useClinicalSectionStatuses(clinicalNoteId);

  /** Refresh validation indicators only after the parent save callback settles. */
  const handleSaved = useCallback(
    async (...args) => {
      try {
        if (typeof onSaved === 'function') {
          await onSaved(...args);
        }
      } finally {
        await refreshSectionStatuses();
      }
    },
    [onSaved, refreshSectionStatuses]
  );

  const commonProps = {
    clinicalNoteId,
    disabled,
    onSaved: handleSaved,
    onVersionChange,
    navigationCoordinator,
  };

  const accordionProps = (key, index) => ({
    sectionKey: `${resetKey}-${key}`,
    accordionGroup,
    previousSectionKey:
      index > 0 ? `${resetKey}-${DAILY_SECTION_KEYS[index - 1]}` : null,
    nextSectionKey:
      index < DAILY_SECTION_KEYS.length - 1
        ? `${resetKey}-${DAILY_SECTION_KEYS[index + 1]}`
        : null,
  });

  return (
    <div className="clinical-note-section-stack">
      <ClinicalNoteCollapsibleSection
        title="Pain"
        defaultCollapsed={false}
        status={statuses.PAIN}
        resetKey={`${resetKey}-pain`}
        {...accordionProps('pain', 0)}
      >
        <ClinicalNotePainSection {...commonProps} />
      </ClinicalNoteCollapsibleSection>

      <ClinicalNoteCollapsibleSection
        title="Range of Motion"
        defaultCollapsed
        status={statuses.ROM}
        resetKey={`${resetKey}-rom`}
        {...accordionProps('rom', 1)}
      >
        <ClinicalNoteRomSection {...commonProps} />
      </ClinicalNoteCollapsibleSection>

      <ClinicalNoteCollapsibleSection
        title="Strength / GMMT"
        defaultCollapsed
        status={statuses.STRENGTH}
        resetKey={`${resetKey}-strength`}
        {...accordionProps('strength', 2)}
      >
        <ClinicalNoteStrengthSection {...commonProps} />
      </ClinicalNoteCollapsibleSection>

      <ClinicalNoteCollapsibleSection
        title="Goals"
        defaultCollapsed
        status={statuses.GOAL}
        resetKey={`${resetKey}-goals`}
        {...accordionProps('goals', 3)}
      >
        <ClinicalNoteGoalSection {...commonProps} />
      </ClinicalNoteCollapsibleSection>

      <ClinicalNoteCollapsibleSection
        title="Clinical Content / Narrative"
        defaultCollapsed
        status={statuses.CONTENT}
        resetKey={`${resetKey}-content`}
        {...accordionProps('content', 4)}
      >
        <ClinicalNoteContentSection {...commonProps} />
      </ClinicalNoteCollapsibleSection>

      <ClinicalNoteCollapsibleSection
        title="Treatment"
        defaultCollapsed
        status={statuses.TREATMENT}
        resetKey={`${resetKey}-treatment`}
        {...accordionProps('treatment', 5)}
      >
        <ClinicalNoteTreatmentSection {...commonProps} />
      </ClinicalNoteCollapsibleSection>
    </div>
  );
}
